import type { ArticlePageData } from "@core/SitePresenter/SitePresenter";
import ArticleRefService from "@core-ui/ContextServices/ArticleRef";
import { useScrollPositionStore } from "@core-ui/stores/ScrollPositionStore";
import { useEffect, useLayoutEffect, useRef } from "react";

const useArticleScrollPosition = (data: ArticlePageData) => {
	const articleRef = ArticleRefService.value;
	const { getPosition, setPosition } = useScrollPositionStore();
	const currentArticlePath = data?.articleProps?.ref?.path;
	const isProgrammaticScrollRef = useRef(false);

	useLayoutEffect(() => {
		const el = articleRef?.current;
		if (!el || !currentArticlePath) return;
		if (window.location.hash.length > 1) return;
		// window.location.hash strips the fragment directive, so check the raw navigation URL
		const navUrl = (performance.getEntriesByType("navigation") as PerformanceNavigationTiming[])[0]?.name ?? "";
		if (navUrl.includes(":~:")) return;

		const savedPosition = getPosition(currentArticlePath);

		isProgrammaticScrollRef.current = true;

		if (!savedPosition) {
			el.scrollTop = 0;
			isProgrammaticScrollRef.current = false;
			return;
		}

		el.scrollTop = savedPosition;

		const positionReached = () => Math.abs(el.scrollTop - savedPosition) <= 1;

		if (positionReached()) {
			isProgrammaticScrollRef.current = false;
			return;
		}

		let active = true;

		const stopRestoring = () => {
			active = false;
			isProgrammaticScrollRef.current = false;
			observer.disconnect();
			clearTimeout(restoreTimeout);
		};

		const retryUntilPositionReached = () => {
			if (!active) return;
			el.scrollTop = savedPosition;
			if (positionReached()) stopRestoring();
		};

		const observer = new MutationObserver(retryUntilPositionReached);
		observer.observe(el, { subtree: true, childList: true, attributes: true });

		const restoreTimeout = setTimeout(stopRestoring, 5000);

		el.addEventListener("wheel", stopRestoring, { once: true, passive: true });
		el.addEventListener("touchstart", stopRestoring, { once: true, passive: true });
		el.addEventListener("pointerdown", stopRestoring, { once: true, passive: true });

		return () => {
			stopRestoring();
			el.removeEventListener("wheel", stopRestoring);
			el.removeEventListener("touchstart", stopRestoring);
			el.removeEventListener("pointerdown", stopRestoring);
		};
	}, [currentArticlePath, articleRef, getPosition]);

	useEffect(() => {
		if (!articleRef?.current || !currentArticlePath) return;

		const saveScrollPosition = () => {
			if (isProgrammaticScrollRef.current) return;
			if (articleRef.current) setPosition(currentArticlePath, articleRef.current.scrollTop);
		};

		const scrollElement = articleRef.current;
		scrollElement.addEventListener("scroll", saveScrollPosition, { passive: true });

		return () => scrollElement.removeEventListener("scroll", saveScrollPosition);
	}, [currentArticlePath, articleRef, setPosition]);
};

export default useArticleScrollPosition;
